// Joining a budget's risk projections onto its period rows.
//
// The FORECAST column was a placeholder for as long as it existed: both places that
// build period rows hardcoded `forecast: null`, and no budget endpoint returned a
// per-period number. It turned out one already did — GET /budgets/{id}/risk carries
// `variance.perPeriod[]`, where `value` is the period's PROJECTED total.
//
// Projected, not raw forecast, and that distinction is the point. A forecast only covers
// days that have not happened yet, so for the period you are currently in it would read
// far below the period's amount — not from an underspend, but because most of that
// period is already actuals. The backend already resolves this per period:
//
//   elapsed period  -> actuals for the whole period            (basis "actual")
//   current period  -> actuals to date + forecast for the rest (basis "actual")
//   future period   -> forecast for the whole period           (basis "forecast")
//
// Reading it from the risk payload rather than computing it again also means this column
// cannot disagree with the Earliest breach and Worst variance cards on the same page:
// all three are derived from the same per-period values.

// perPeriodByLabel indexes risk.variance.perPeriod by its period label.
//
// Tolerates every missing level, because risk loads independently of the periods and is
// simply absent on first paint — and a budget page must render before its risk arrives.
export function perPeriodByLabel(risk) {
  const rows = risk?.variance?.perPeriod;
  if (!Array.isArray(rows)) return {};
  const out = {};
  for (const row of rows) {
    if (row && row.period != null) out[String(row.period)] = row;
  }
  return out;
}

// mergePeriodForecast returns `periods` with `forecast` / `forecastBasis` filled from the
// matching risk projection.
//
// `hasValue: false` stays null rather than becoming 0: the backend sets it when nothing
// covered that period at all, and a confident "€ 0,00" there would claim a budget is
// forecast to spend nothing when the truth is that we do not know. A genuine zero — a
// scope that matches no cost — arrives with hasValue true and renders as 0.
export function mergePeriodForecast(periods, risk) {
  if (!Array.isArray(periods) || periods.length === 0) return periods;
  const byLabel = perPeriodByLabel(risk);
  if (Object.keys(byLabel).length === 0) return periods;

  return periods.map((p) => {
    const key = p.label ?? p.periodKey ?? p.id;
    const row = byLabel[String(key)];
    if (!row || row.hasValue === false) return p;
    if (typeof row.value !== "number") return p;
    return { ...p, forecast: row.value, forecastBasis: row.basis };
  });
}

// forecastTitle explains which basis produced a cell, so a number under a column headed
// "Forecast" that is really actuals-to-date can say so on hover instead of misleading.
export function forecastTitle(basis) {
  if (basis === "forecast") return "Forecast for the full period";
  if (basis === "actual") return "Actuals to date plus the forecast for the remaining days";
  return undefined;
}
